import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Search, X, FileText, ArrowRight, Bot } from 'lucide-react';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';
import { api, ChatSource } from '@/lib/api';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ChatState = 'idle' | 'loading' | 'done' | 'error';

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [chatState, setChatState] = useState<ChatState>('idle');
  const [chatAnswer, setChatAnswer] = useState('');
  const [chatSources, setChatSources] = useState<ChatSource[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { results, isSearching } = useGlobalSearch(query, 6);

  const knownRoots = ['auth', 'dashboard', 'search', 'admin', 'forbidden'];
  const segments = pathname.split('/').filter(Boolean);
  const isDocPage = segments.length >= 2 && !knownRoots.includes(segments[0]);
  const docOrg = isDocPage ? segments[0] : null;
  const docCollection = isDocPage ? segments[1] : null;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setChatState('idle');
    setChatAnswer('');
    setChatSources([]);
  }, [query]);

  async function handleAskAI() {
    if (!docOrg || !docCollection || !query.trim()) return;
    setChatState('loading');
    try {
      const data = await api.chat(docOrg, docCollection, query.trim());
      setChatAnswer(data.answer || 'No answer found in the documentation.');
      setChatSources(data.sources);
      setChatState('done');
    } catch {
      setChatState('error');
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onClose();
    setQuery('');
  };

  const handleDisplayAll = () => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
    onClose();
    setQuery('');
  };

  const highlightMatch = (text: string, searchQuery: string) => {
    if (!searchQuery.trim()) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-primary/30 text-foreground rounded px-0.5">{part}</mark>
      ) : part
    );
  };

  // MEDIUM-5: converteer [[HL]]/[[/HL]] API-markers naar <mark> — nooit als HTML renderen
  const renderSnippet = (snippet: string) => {
    const parts = snippet.split(/(\[\[HL\]\]|\[\[\/HL\]\])/);
    let inMark = false;
    return parts.map((part, i) => {
      if (part === '[[HL]]') { inMark = true; return null; }
      if (part === '[[/HL]]') { inMark = false; return null; }
      return inMark
        ? <mark key={i} className="bg-primary/30 text-foreground rounded px-0.5">{part}</mark>
        : part;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      {/* Chat Bubble Panel */}
      <div 
        ref={panelRef}
        className={`
          pointer-events-auto
          fixed bottom-24 right-6
          w-[380px] max-w-[calc(100vw-3rem)]
          bg-card border border-border rounded-2xl shadow-2xl
          overflow-hidden
          transform-gpu origin-bottom-right
          transition-all duration-300 ease-out
          ${isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}
        `}
        style={{
          boxShadow: '0 10px 40px -10px hsl(var(--primary) / 0.2), 0 20px 60px -20px rgba(0,0,0,0.3)'
        }}
      >
        {/* Speech bubble tail */}
        <div 
          className="absolute -bottom-2 right-8 w-4 h-4 bg-card border-r border-b border-border transform rotate-45"
          style={{ zIndex: -1 }}
        />

        {/* Search Input */}
        <div className="flex items-center gap-3 p-3 border-b border-border bg-muted/30">
          <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-sm"
          />
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {query.trim() === '' ? (
            <div className="p-6 text-center text-muted-foreground">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-primary/10 flex items-center justify-center">
                <Search className="w-5 h-5 text-primary" />
              </div>
              <p className="text-sm">What are you looking for?</p>
            </div>
          ) : isSearching ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
              <p className="mt-2 text-xs text-muted-foreground">Searching...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4">
              {chatState === 'idle' && (
                <div className="text-center text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm mb-3">No results for "{query}"</p>
                  {isDocPage && (
                    <button
                      onClick={handleAskAI}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      <Bot className="w-4 h-4" />
                      Ask AI about this
                    </button>
                  )}
                </div>
              )}
              {chatState === 'loading' && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
                  <p className="mt-2 text-xs text-muted-foreground">Asking AI...</p>
                </div>
              )}
              {chatState === 'done' && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2 text-xs">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                    <span className="text-primary font-medium">AI Answer</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{chatAnswer}</p>
                  {chatSources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-[10px] text-muted-foreground mb-1.5">Sources:</p>
                      <div className="flex flex-col gap-1">
                        {chatSources.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/${docOrg}/${docCollection}/${s.slug}`}
                            onClick={onClose}
                            className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                          >
                            <FileText className="w-3 h-3 flex-shrink-0" />
                            {s.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setChatState('idle')}
                    className="mt-3 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  >
                    ← Back to search
                  </button>
                </div>
              )}
              {chatState === 'error' && (
                <div className="text-center text-muted-foreground">
                  <p className="text-sm mb-2">AI could not answer this question.</p>
                  <button onClick={handleAskAI} className="text-xs text-primary hover:underline">
                    Try again
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {results.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className="w-full p-3 text-left hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors truncate">
                        {highlightMatch(result.title, query)}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {renderSnippet(result.snippet)}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full">
                          {result.tenant}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {result.docType}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Display All Button */}
        {query.trim() !== '' && results.length > 0 && (
          <div className="p-2 border-t border-border bg-muted/20">
            <button
              onClick={handleDisplayAll}
              className="w-full py-2 px-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              View All Results
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Keyboard hint */}
        <div className="px-3 py-1.5 border-t border-border bg-muted/10 text-[10px] text-muted-foreground text-center">
          Press <kbd className="px-1 py-0.5 bg-muted rounded border border-border font-mono">Esc</kbd> to close
        </div>
      </div>
    </div>
  );
}
